import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Chip from '../../chip/chip';

const DEFAULT_SIZES = '(max-width: 375px) 180px, (max-width: 768px) 240px, (max-width: 1028px) 480px, 640w';

const CardMediaChipWrapper = styled.div`
	position: absolute;
	left: 0;
	top: 15%;
`;

interface CardMediaWrapperProps {
	children?: React.ReactNode | React.ReactNode[];
	margin: string;
}

const CardMediaWrapper = styled.div<CardMediaWrapperProps>`
	display: flex;
	flex-direction: column;
	position: relative;
	cursor: ${({ onClick }) => (onClick ? 'pointer' : 'inherit')};
	height: 100%;
	width: 100%;
	flex: 1;
	margin: ${({ margin }) => margin};
`;

interface CardMediaImgProps {
	className?: string;
}

const CardMediaImg = styled.img<CardMediaImgProps>`
	max-width: 100%;
`;

interface CardMediaBottomContentProps {
	justifyContent?: string;
}

interface ForegroundMediaProps {
	layeredMediaBottomHeight?: number;
}

const CardMediaBottomContent = styled.div<CardMediaBottomContentProps>`
	position: absolute;
	bottom: 0;
	width: 100%;
	display: flex;
	justify-content: ${({ justifyContent }) => justifyContent || 'left'};
`;

const CardMediaPill = styled.div`
	background-color: var(--card-media-pill-bg, var(--card-bg, var(--box-bg)));
	height: 20px;
	font: var(--card-media-pill-font, var(--caption));
	padding: 2px 6px;
	margin: 10px;
	border-radius: var(--card-media-pill-border-radius, 12px);
`;

const IconRightWrapper = styled.div`
	pointer-events: none;
	position: absolute;
	width: 36px;
	height: 36px;
	top: -3px;
	right: 12px;
	background: var(--card-media-icon-right-bg, rgba(var(--secondary), 1));
	display: flex;
	align-content: center;
	justify-content: center;
	padding-top: 7px;
	border-radius: 3px;
	z-index: 2;
	&::after {
		border-top: 8px solid var(--card-media-icon-right-bg, rgba(var(--secondary), 1));
		border-left: 18px solid transparent;
		border-right: 18px solid transparent;
		bottom: -11px;
		height: 12px;
		position: absolute;
		width: 36px;
		border-radius: 3px;
		content: '';
	}
	svg {
		fill: var(--card-media-icon-right-fill, rgba(var(--base-02)));
	}
`;

const BannerSVG = styled.svg`
	border-top-left-radius: 3px;
	fill: var(--card-media-banner-text-bg, rgba(var(--primary), 1));
	left: 0;
	position: absolute;
	top: 0;
	z-index: 1;
	text {
		fill: var(--card-media-banner-text, rgba(var(--base-02), 1));
		font: var(--caption);
		text-transform: uppercase;
	}
`;

const VisibilityWrapper = styled.div<{ visible: boolean }>`
	display: inline-block;
	visibility: ${props => (props.visible ? 'visible' : 'hidden')};
	width: ${props => (props.visible ? '100%' : '0px')};
	height: ${props => (props.visible ? '100%' : '0px')};
`;

const LayeredMedia = styled.div`
	display: flex;
	position: relative;
	height: 100%;
	align-items: center;
	justify-content: center;
`;

const ForegroundMedia = styled.div<ForegroundMediaProps>`
	position: relative;
	top: ${({ layeredMediaBottomHeight }) => {
		return layeredMediaBottomHeight ? `calc(-${layeredMediaBottomHeight}px / 2)` : 0; // vertically center for grid-type 4
	}};
	height: ${({ layeredMediaBottomHeight }) => {
		return `calc(100% - ${layeredMediaBottomHeight || 0}px)`;
	}};
	img {
		object-fit: contain; // vertically center for grid-type 4
	}
`;

const BackgroundMedia = styled.div`
	position: absolute;
	z-index: 0;
	width: 100%;
	height: 100%;
`;

interface Props {
	onClick?: () => void;
	src?: string;
	srcSet?: string;
	sizes?: string;
	pillContent?: React.ReactNode;
	iconRight?: React.ReactNode;
	bannerText?: string;
	lazyload?: boolean;
	chipText?: string;
	chipIcon?: React.ReactNode;
	imageErrorHandler?: () => void;
	imageComponent?: React.ReactNode;
	imageHandler?: () => React.ReactNode;
	dataAt?: string;
	iconDataAt?: string;
	videoComponent?: React.ReactNode;
	showVideo?: boolean;
	wrapperMargin?: string;
	layeredMediaVisibility?: boolean;
	layeredMediaBottomHeight?: number;
}

/**
 * `CardMedia` is used inside a `Card` to load an image for the card.
 *
 * @param {Boolean} lazyload determines if we should enable lazyloading with the 'LazySizes'-lib.
 * If not, we use what native solutions there are available for srcSet's and lazyloading images.
 *
 * @param {React.ReactNode} imageComponent makes it possible to provide your own image components.
 * For example if you want to use some other element (picture), or want to style it totally different.
 * If no imageComponent is provided we use a regular img-element with/without lazy loading.
 */
const CardMedia: FC<Props> = ({
	onClick,
	src,
	srcSet,
	sizes = DEFAULT_SIZES,
	pillContent,
	iconRight,
	bannerText,
	lazyload,
	chipText,
	chipIcon,
	imageErrorHandler,
	imageComponent,
	imageHandler,
	dataAt,
	iconDataAt,
	videoComponent,
	showVideo,
	wrapperMargin = '0',
	layeredMediaVisibility = false,
	layeredMediaBottomHeight = 36
}) => {
	const [imageVisible, setImageVisible] = useState(true);
	const [videoVisible, setVideoVisible] = useState(false);

	useEffect(() => {
		if (videoComponent && showVideo) {
			setImageVisible(false);
			setVideoVisible(true);
		}
	}, [videoComponent, showVideo]);

	return (
		<>
			{iconRight && <IconRightWrapper>{iconRight}</IconRightWrapper>}

			<CardMediaWrapper onClick={onClick} margin={wrapperMargin}>
				{bannerText && (
					<BannerSVG>
						<polygon points="0 0, 72 0, 0 72" />
						<text textAnchor="middle" x="32" y="24" transform="rotate(-45 36 36)">
							{bannerText}
						</text>
					</BannerSVG>
				)}

				{!imageComponent &&
					(imageHandler ? (
						imageHandler()
					) : (
						<CardMediaImg
							src={src}
							srcSet={!lazyload ? srcSet : undefined}
							data-srcset={lazyload ? srcSet : undefined}
							sizes={sizes}
							onError={imageErrorHandler}
							className={lazyload ? 'lazyload' : ''}
							loading="lazy"
						/>
					))}

				{imageComponent && (
					<VisibilityWrapper visible={imageVisible}>
						{layeredMediaVisibility ? (
							<LayeredMedia>
								<BackgroundMedia>{imageComponent}</BackgroundMedia>
								<ForegroundMedia layeredMediaBottomHeight={layeredMediaBottomHeight}>
									{imageComponent}
								</ForegroundMedia>
							</LayeredMedia>
						) : (
							imageComponent
						)}
					</VisibilityWrapper>
				)}

				{videoComponent && (
					<VisibilityWrapper visible={videoVisible}>
						{layeredMediaVisibility ? (
							<LayeredMedia>
								<BackgroundMedia>{videoComponent}</BackgroundMedia>
								<ForegroundMedia layeredMediaBottomHeight={layeredMediaBottomHeight}>
									{videoComponent}
								</ForegroundMedia>
							</LayeredMedia>
						) : (
							videoComponent
						)}
					</VisibilityWrapper>
				)}

				{pillContent && (
					<CardMediaBottomContent justifyContent="center">
						<CardMediaPill>{pillContent}</CardMediaPill>
					</CardMediaBottomContent>
				)}

				{chipText && (
					<CardMediaChipWrapper>
						<Chip
							dataAt={dataAt}
							borderBottomLeftRadius="0px"
							borderTopLeftRadius="0px"
							icon={chipIcon}
							iconDataAt={iconDataAt}
						>
							{chipText}
						</Chip>
					</CardMediaChipWrapper>
				)}
			</CardMediaWrapper>
		</>
	);
};

export default CardMedia;
